/*----------------------------------
- DOMAINS FILTER RAIL
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 * @adr  ADR-0035
 * @rule The rail renders the member filter controls but never applies them. Accordions expand
 *       freely; any rule change or locked-filter click opens authentication with the intended
 *       query in `returnTo`, and rows and URL never change.
 *
 * The exact `BrowseFilterSidebar` Browse renders, so the visitor meets the real tool rather than
 * a mock of it. The facet props are empty because this page has no aggregate pipeline: counts
 * belong to an authenticated search.
 */

import React from 'react';

import BrowseFilterSidebar, {
    DEFAULT_BROWSE_EXPANDED_FILTER_IDS,
} from '@uniquedomains/components/radar/controls/BrowseFilterSidebar';
import type { RadarFilterDef, RadarQuery } from '@uniquedomains/components/radar/rules/types';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsFilterRailProps = {
    filterCatalog: RadarFilterDef[];
    query: RadarQuery;
    onFilterIntent: (nextQuery: RadarQuery) => void;
    onLockedFilter: (filter: { id: string; label: string }) => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const noop = () => {};

const DomainsFilterRail = ({
    filterCatalog,
    query,
    onFilterIntent,
    onLockedFilter,
}: DomainsFilterRailProps) => {
    const [expandedFilterIds, setExpandedFilterIds] = React.useState<string[]>(() => [
        ...new Set([
            ...DEFAULT_BROWSE_EXPANDED_FILTER_IDS,
            ...query.rules.map((rule) => rule.filterId),
        ]),
    ]);

    return (
        <div data-testid="domains-filter-rail">
            <BrowseFilterSidebar
                /*
                 * No counts anywhere on this surface: nothing here re-queries, so a total or a
                 * per-option count would describe a search the page cannot run. `idle` with a null
                 * total leaves every count affordance unrendered rather than pending.
                 */
                showSummary={false}
                state="idle"
                totalCount={null}
                facets={{}}
                facetStates={{}}
                retryableFacetIds={[]}
                filters={filterCatalog}
                query={query}
                /* Advanced filters stay locked for a visitor: each one opens the signup wall (ADR-0083). */
                canUseAdvancedFilters={false}
                expandedFilterIds={expandedFilterIds}
                canRetryCounts={false}
                onQueryChange={onFilterIntent}
                onLockedFilterClick={onLockedFilter}
                onExpandedFilterIdsChange={setExpandedFilterIds}
                onFacetRequested={noop}
                onRetryFacet={noop}
                onRetryCounts={noop}
            />
        </div>
    );
};

export default DomainsFilterRail;
